import React, { useEffect, useMemo, useRef, useState } from 'react';
import './MonthlyPlanner.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { IconButton } from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';

type Task = {
  id: string;
  taskName: string;
  assignee: string;
  effort: string;
  referenceId: string;
  comment: string;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
};

type DayTasks = Record<string, Task[]>; // key: YYYY-MM-DD

function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getDaysInMonth(year: number, monthIndex0: number): Date[] {
  const first = new Date(year, monthIndex0, 1);
  const days: Date[] = [];
  let current = first;
  while (current.getMonth() === monthIndex0) {
    days.push(new Date(current));
    current = new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1);
  }
  return days;
}

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export default function MonthlyPlanner(): React.ReactElement {
  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [monthIndex, setMonthIndex] = useState<number>(today.getMonth()); // 0-11
  const [dayTasks, setDayTasks] = useState<DayTasks>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [activeDay, setActiveDay] = useState<string | null>(null); // YYYY-MM-DD
  const [form, setForm] = useState<Omit<Task, 'id'>>({ taskName: '', assignee: '', effort: '', referenceId: '', comment: '', startDate: '', endDate: '' });
  const [bulkText, setBulkText] = useState<string>('');
  const [bulkAssignee, setBulkAssignee] = useState<string>('');

  const days = useMemo(() => getDaysInMonth(year, monthIndex), [year, monthIndex]);

  // Storage keys and helpers
  const STORAGE_KEY = 'monthlyPlanner.tasks.v1';
  const STORAGE_TEAM_KEY = 'monthlyPlanner.teamMembers.v1';
  const LAST_ASSIGNEE_KEY = 'monthlyPlanner.lastAssignee.v1';

  const [teamMembers, setTeamMembers] = useState<string[]>([]);
  const [teamHydrated, setTeamHydrated] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState('');
  /** When false, flat table, CSV, and PDF omit Saturday & Sunday columns (weekdays only). */
  const [showWeekends, setShowWeekends] = useState(false);

  function normalizeDayTasks(input: DayTasks): DayTasks {
    const output: DayTasks = {};
    for (const [dateKey, tasks] of Object.entries(input)) {
      output[dateKey] = tasks.map(t => ({
        id: t.id,
        taskName: t.taskName,
        assignee: t.assignee,
        effort: t.effort,
        referenceId: t.referenceId,
        comment: t.comment,
        startDate: (t as any).startDate ?? dateKey,
        endDate: (t as any).endDate ?? dateKey,
      }));
    }
    return output;
  }

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      setDayTasks(normalizeDayTasks(parsed as DayTasks));
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_TEAM_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const names = parsed
            .filter((x): x is string => typeof x === 'string')
            .map(s => s.trim())
            .filter(Boolean);
          setTeamMembers(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
        }
      }
    } catch {
      /* ignore */
    } finally {
      setTeamHydrated(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dayTasks));
  }, [dayTasks]);

  useEffect(() => {
    if (!teamHydrated) return;
    localStorage.setItem(STORAGE_TEAM_KEY, JSON.stringify(teamMembers));
  }, [teamMembers, teamHydrated]);

  function addTeamMember() {
    const name = newTeamMember.trim();
    if (!name) return;
    setTeamMembers(prev => {
      if (prev.includes(name)) return prev;
      return [...prev, name].sort((a, b) => a.localeCompare(b));
    });
    setNewTeamMember('');
  }

  function removeTeamMember(name: string) {
    setTeamMembers(prev => prev.filter(n => n !== name));
    if (form.assignee === name) setForm(f => ({ ...f, assignee: '' }));
    if (bulkAssignee === name) setBulkAssignee('');
  }

  function handleAdd(dayKey: string) {
    if (!form.taskName.trim()) return;
    setDayTasks(prev => {
      const existing = prev[dayKey] ?? [];
      const start = form.startDate || dayKey;
      const end = form.endDate || dayKey;
      const newTask: Task = { id: `${Date.now()}-${Math.random().toString(36).slice(2,8)}`, taskName: form.taskName.trim(), assignee: form.assignee.trim(), effort: form.effort, referenceId: form.referenceId, comment: form.comment, startDate: start, endDate: end };
      return { ...prev, [dayKey]: [...existing, newTask] };
    });
    // Keep assignee (and dates/effort) for rapid back-to-back adds; only reset task text fields
    setForm(prev => {
      const a = prev.assignee.trim();
      if (a) {
        try {
          localStorage.setItem(LAST_ASSIGNEE_KEY, a);
        } catch {
          /* ignore */
        }
      }
      return {
        ...prev,
        taskName: '',
        referenceId: '',
        comment: '',
        startDate: dayKey,
        endDate: dayKey,
      };
    });
  }

  function handleRemove(dayKey: string, taskId: string) {
    setDayTasks(prev => {
      const remaining = (prev[dayKey] ?? []).filter(t => t.id !== taskId);
      const next: DayTasks = { ...prev };
      if (remaining.length) next[dayKey] = remaining; else delete next[dayKey];
      return next;
    });
  }

  function openDayModal(date: Date) {
    const key = formatDateKey(date);
    setActiveDay(key);
    setForm(prev => {
      let assignee = prev.assignee;
      if (!assignee.trim()) {
        try {
          const saved = localStorage.getItem(LAST_ASSIGNEE_KEY);
          if (saved) assignee = saved;
        } catch {
          /* ignore */
        }
      }
      return { ...prev, assignee, startDate: key, endDate: key };
    });
    setBulkAssignee(prev => {
      if (prev.trim()) return prev;
      try {
        const saved = localStorage.getItem(LAST_ASSIGNEE_KEY);
        return saved || '';
      } catch {
        return prev;
      }
    });
  }

  function closeModal() {
    setActiveDay(null);
    setForm({ taskName: '', assignee: '', effort: '', referenceId: '', comment: '', startDate: '', endDate: '' });
    setBulkText('');
    setBulkAssignee('');
  }

  function exportTasks() {
    const dataStr = JSON.stringify(dayTasks, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const ts = new Date();
    const name = `monthly-planner-${ts.getFullYear()}-${(ts.getMonth()+1).toString().padStart(2,'0')}-${ts.getDate().toString().padStart(2,'0')}.json`;
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  function exportToPDF() {
    const { assignees, dateKeys, tasksByAssigneeDate } = flatTableData;
    const monthName = monthOptions[monthIndex];

    if (assignees.length === 0) {
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.text('Monthly Planner Report', 20, 20);
      doc.setFontSize(14);
      doc.text(`${monthName} ${year}`, 20, 35);
      doc.setFontSize(12);
      doc.text('No tasks found for this month.', 20, 55);
      doc.save(`monthly-planner-report-${monthName.toLowerCase()}-${year}.pdf`);
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const ts = new Date();
    const margin = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const usableWidth = pageWidth - margin * 2;
    const assigneeColWidth = 40;

    const weeks: string[][] = [];
    let currentWeek: string[] = [];
    for (const dk of dateKeys) {
      const d = new Date(dk + 'T00:00:00');
      if (d.getDay() === 0 && currentWeek.length > 0) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      currentWeek.push(dk);
    }
    if (currentWeek.length > 0) weeks.push(currentWeek);

    let pdfPageIndex = 0;
    weeks.forEach((weekDates) => {
      const weekDatesFiltered = showWeekends
        ? weekDates
        : weekDates.filter(dk => {
            const dow = new Date(dk + 'T12:00:00').getDay();
            return dow !== 0 && dow !== 6;
          });
      if (weekDatesFiltered.length === 0) return;

      if (pdfPageIndex > 0) doc.addPage();
      pdfPageIndex += 1;

      const weekStart = weekDatesFiltered[0];
      const weekEnd = weekDatesFiltered[weekDatesFiltered.length - 1];

      doc.setFontSize(16);
      doc.text(`${monthName} ${year} — Weekly Planner`, margin, 13);
      doc.setFontSize(10);
      doc.text(`Week ${pdfPageIndex}: ${weekStart} to ${weekEnd}`, margin, 20);
      doc.setFontSize(8);
      doc.text(`Generated on: ${ts.toLocaleDateString()}`, margin, 26);
      if (!showWeekends) {
        doc.text('Weekends hidden (weekdays only)', margin, 31);
      }

      const dateHeaders = weekDatesFiltered.map(dk => {
        const d = new Date(dk + 'T12:00:00');
        const dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
        return `${d.getDate()} ${monthOptions[d.getMonth()].slice(0, 3)}\n${dayName}`;
      });

      const head = [['Assignee', ...dateHeaders]];

      const body = assignees.map(assignee => {
        const cells = weekDatesFiltered.map(dk => {
          const tasks = tasksByAssigneeDate[assignee]?.[dk];
          return tasks ? tasks.join('\n') : '';
        });
        return [assignee, ...cells];
      });

      const dateColWidth = (usableWidth - assigneeColWidth) / weekDatesFiltered.length;
      const colStyles: Record<number, { cellWidth: number; halign?: 'left' | 'center' | 'right' }> = {
        0: { cellWidth: assigneeColWidth },
      };
      for (let i = 1; i <= weekDatesFiltered.length; i++) {
        colStyles[i] = { cellWidth: dateColWidth, halign: 'center' };
      }

      const weekendCols = new Set<number>();
      weekDatesFiltered.forEach((dk, i) => {
        const d = new Date(dk + 'T12:00:00');
        if (d.getDay() === 0 || d.getDay() === 6) weekendCols.add(i + 1);
      });

      autoTable(doc, {
        head,
        body,
        startY: showWeekends ? 30 : 34,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          lineWidth: 0.2,
          lineColor: [200, 200, 200],
          overflow: 'linebreak',
          valign: 'top',
        },
        headStyles: {
          fillColor: [30, 64, 175],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center',
          valign: 'middle',
          cellPadding: 3,
        },
        bodyStyles: {
          textColor: [30, 30, 30],
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        columnStyles: colStyles,
        margin: { left: margin, right: margin },
        didParseCell: (data: any) => {
          if (data.section === 'body' && weekendCols.has(data.column.index)) {
            data.cell.styles.fillColor = data.row.index % 2 === 0
              ? [245, 240, 255]
              : [238, 232, 250];
          }
          if (data.section === 'head' && weekendCols.has(data.column.index)) {
            data.cell.styles.fillColor = [124, 58, 237];
          }
          if (data.section === 'body' && data.column.index === 0) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = [30, 64, 175];
          }
        },
      });
    });

    doc.save(`monthly-planner-report-${monthName.toLowerCase()}-${year}.pdf`);
  }

  function triggerImport() {
    fileInputRef.current?.click();
  }

  function isValidTask(obj: unknown): obj is Task {
    if (!obj || typeof obj !== 'object') return false;
    const t = obj as Partial<Task>;
    return (
      typeof t.id === 'string' &&
      typeof t.taskName === 'string' &&
      typeof t.assignee === 'string' &&
      typeof t.effort === 'string' &&
      typeof t.referenceId === 'string' &&
      typeof t.comment === 'string' &&
      (typeof (t as any).startDate === 'undefined' || typeof (t as any).startDate === 'string') &&
      (typeof (t as any).endDate === 'undefined' || typeof (t as any).endDate === 'string')
    );
  }

  function validateImportedData(data: unknown): data is DayTasks {
    if (!data || typeof data !== 'object') return false;
    const obj = data as Record<string, unknown>;
    const dateKeyOk = Object.keys(obj).every(k => /^\d{4}-\d{2}-\d{2}$/.test(k));
    if (!dateKeyOk) return false;
    return Object.values(obj).every(v => Array.isArray(v) && (v as unknown[]).every(isValidTask));
  }

  function onImportFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (validateImportedData(parsed)) {
          setDayTasks(normalizeDayTasks(parsed));
        } else {
          alert('Invalid tasks file format.');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Import failed to parse JSON', err);
        alert('Failed to parse JSON file.');
      } finally {
        fileInputRef.current?.setAttribute('value', '');
      }
    };
    reader.readAsText(file);
  }

  const monthOptions = [
    'January','February','March','April','May','June','July','August','September','October','November','December'
  ];

  const flatTableData = useMemo(() => {
    const assigneeSet = new Set<string>();
    const tasksByAssigneeDate: Record<string, Record<string, string[]>> = {};

    for (const [dateKey, tasks] of Object.entries(dayTasks)) {
      for (const t of tasks) {
        const assignee = t.assignee || 'Unassigned';
        assigneeSet.add(assignee);
        if (!tasksByAssigneeDate[assignee]) tasksByAssigneeDate[assignee] = {};
        if (!tasksByAssigneeDate[assignee][dateKey]) tasksByAssigneeDate[assignee][dateKey] = [];
        tasksByAssigneeDate[assignee][dateKey].push(t.taskName);
      }
    }

    const assignees = Array.from(assigneeSet).sort();
    const dateKeys = days.map(d => formatDateKey(d));
    return { assignees, dateKeys, tasksByAssigneeDate };
  }, [dayTasks, days]);

  const columnDateKeys = useMemo(() => {
    if (showWeekends) return flatTableData.dateKeys;
    return flatTableData.dateKeys.filter(dk => {
      const dow = new Date(dk + 'T12:00:00').getDay();
      return dow !== 0 && dow !== 6;
    });
  }, [flatTableData.dateKeys, showWeekends]);

  function exportCSV() {
    const { assignees, tasksByAssigneeDate } = flatTableData;
    const dateKeys = columnDateKeys;
    if (assignees.length === 0) {
      alert('No tasks to export.');
      return;
    }

    const header = ['Assignee', ...dateKeys.map(dk => {
      const d = new Date(dk + 'T00:00:00');
      return `${d.getDate()} ${monthOptions[d.getMonth()].slice(0, 3)}`;
    })];

    const rows = assignees.map(assignee => {
      const cells = dateKeys.map(dk => {
        const tasks = tasksByAssigneeDate[assignee]?.[dk];
        return tasks ? tasks.join('; ') : '';
      });
      return [assignee, ...cells];
    });

    const csvContent = [header, ...rows]
      .map(row => row.map(escapeCSV).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `monthly-planner-${monthOptions[monthIndex].toLowerCase()}-${year}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  function handleBulkAdd(dayKey: string) {
    const lines = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return;

    const a = bulkAssignee.trim();
    if (a) {
      try {
        localStorage.setItem(LAST_ASSIGNEE_KEY, a);
      } catch {
        /* ignore */
      }
    }

    setDayTasks(prev => {
      const existing = prev[dayKey] ?? [];
      const newTasks: Task[] = lines.map(line => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        taskName: line,
        assignee: bulkAssignee,
        effort: '',
        referenceId: '',
        comment: '',
        startDate: dayKey,
        endDate: dayKey,
      }));
      return { ...prev, [dayKey]: [...existing, ...newTasks] };
    });
    setBulkText('');
  }

  function onAssigneeSelectChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const v = e.target.value;
    if (v === '') {
      setForm(f => ({ ...f, assignee: '' }));
      return;
    }
    if (v === '__other__') {
      setForm(f => ({
        ...f,
        assignee: f.assignee && !teamMembers.includes(f.assignee) ? f.assignee : '',
      }));
      return;
    }
    setForm(f => ({ ...f, assignee: v }));
  }

  function onBulkAssigneeSelectChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const v = e.target.value;
    if (v === '') {
      setBulkAssignee('');
      return;
    }
    if (v === '__other__') {
      setBulkAssignee(prev => (prev && !teamMembers.includes(prev) ? prev : ''));
      return;
    }
    setBulkAssignee(v);
  }

  const assigneeSelectValue =
    form.assignee === '' ? '' : teamMembers.includes(form.assignee) ? form.assignee : '__other__';

  const bulkAssigneeSelectValue =
    bulkAssignee === '' ? '' : teamMembers.includes(bulkAssignee) ? bulkAssignee : '__other__';

  function handleClearAllTasks() {
    if (!window.confirm('Clear all tasks for every day? This cannot be undone.')) return;
    setDayTasks({});
  }

  function handleClearDayTasks(dayKey: string) {
    const count = dayTasks[dayKey]?.length ?? 0;
    if (count === 0) return;
    if (!window.confirm(`Remove all ${count} task${count === 1 ? '' : 's'} on ${dayKey}?`)) return;
    setDayTasks(prev => {
      const next = { ...prev };
      delete next[dayKey];
      return next;
    });
  }

  function handleClearAllTeamMembers() {
    if (teamMembers.length === 0) return;
    if (!window.confirm('Remove every teammate from your saved list?')) return;
    const removed = new Set(teamMembers);
    setTeamMembers([]);
    setForm(f => (f.assignee && removed.has(f.assignee) ? { ...f, assignee: '' } : f));
    setBulkAssignee(b => (b && removed.has(b) ? '' : b));
  }

  return (
    <div className="mp-root">
      <IconButton
        href="https://github.com/release-pilot/release-pilot-public/tree/master/monthly-planner"
        target="_blank"
        rel="noopener"
        color="primary"
        style={{ position: 'absolute', top: 0, right: 0, margin: '10px', zIndex: 1000 }}
      >
        <GitHubIcon fontSize="large" />
      </IconButton>
      <div className="mp-header">
        <img className="mp-logo" src="/assets/Single_mainlogo_tm.svg" alt="Release Pilot" />
        <h2 className="mp-title">Monthly Planner</h2>
      </div>
      <div className="mp-controls">
        <select value={monthIndex} onChange={e => setMonthIndex(parseInt(e.target.value, 10))}>
          {monthOptions.map((label, idx) => (
            <option key={label} value={idx}>{label}</option>
          ))}
        </select>
        <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value || `${today.getFullYear()}`, 10))} style={{ width: 100 }} />
        <button className="mp-primary-btn" onClick={exportTasks}>Export JSON</button>
        <button className="mp-primary-btn" onClick={exportToPDF}>Export PDF</button>
        <button className="mp-primary-btn mp-csv-btn" onClick={exportCSV}>Export CSV</button>
        <button className="mp-add-btn" onClick={triggerImport}>Import</button>
        <button type="button" className="mp-danger-btn" onClick={handleClearAllTasks} title="Remove every task on every day">
          Clear all tasks
        </button>
        <input ref={fileInputRef} type="file" accept="application/json" onChange={onImportFileChange} style={{ display: 'none' }} />
      </div>

      <div className="mp-view-options">
        <span className="mp-view-options-label">Overview columns</span>
        <label className="mp-weekends-toggle">
          <input
            type="checkbox"
            checked={showWeekends}
            onChange={e => setShowWeekends(e.target.checked)}
          />
          Show Saturday &amp; Sunday in table, CSV &amp; PDF
        </label>
      </div>

      <div className="mp-team-panel">
        <div className="mp-team-header">
          <div className="mp-team-header-text">
            <strong className="mp-team-title">Team members</strong>
            <span className="mp-team-hint">Saved in this browser — pick from the list when adding tasks</span>
          </div>
          {teamMembers.length > 0 ? (
            <button type="button" className="mp-danger-btn mp-btn-compact" onClick={handleClearAllTeamMembers}>
              Clear all team
            </button>
          ) : null}
        </div>
        <div className="mp-team-row">
          <input
            className="mp-team-input"
            value={newTeamMember}
            onChange={e => setNewTeamMember(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTeamMember();
              }
            }}
            placeholder="Add teammate name (Enter to add)"
          />
          <button type="button" className="mp-add-btn" onClick={addTeamMember}>Add</button>
        </div>
        {teamMembers.length > 0 ? (
          <div className="mp-team-chips">
            {teamMembers.map(name => (
              <span key={name} className="mp-team-chip">
                {name}
                <button type="button" className="mp-team-chip-remove" aria-label={`Remove ${name}`} onClick={() => removeTeamMember(name)}>×</button>
              </span>
            ))}
          </div>
        ) : (
          <p className="mp-team-empty">No teammates yet — add names above to fill the assignee dropdown.</p>
        )}
      </div>

      <div className="mp-grid">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} className="mp-weekday">{d}</div>
        ))}

        {(() => {
          const firstDay = new Date(year, monthIndex, 1).getDay();
          const blanks = Array.from({ length: firstDay }, (_, i) => <div key={`b-${i}`} />);
          const cells = days.map(date => {
            const key = formatDateKey(date);
            const count = dayTasks[key]?.length ?? 0;
            return (
              <div key={key} className="mp-cell">
                <div className="mp-cell-header">
                  <div className="mp-day">
                    {date.getDate()}
                    {count > 0 ? <span className="mp-task-dot" /> : null}
                  </div>
                  <button className="mp-add-btn" onClick={() => openDayModal(date)}>Add</button>
                </div>
                <div className="mp-count">
                  {(() => {
                    if (count === 0) return 'No tasks';
                    const suffix = count > 1 ? 's' : '';
                    return `${count} task${suffix}`;
                  })()}
                </div>
              </div>
            );
          });
          return [...blanks, ...cells];
        })()}
      </div>

      {activeDay && (
        <div className="mp-overlay">
          <div className="mp-modal">
            <div className="mp-modal-header">
              <h3 className="mp-modal-title">Tasks on {activeDay}</h3>
              <div className="mp-modal-actions">
                {(dayTasks[activeDay] ?? []).length > 0 ? (
                  <button
                    type="button"
                    className="mp-danger-btn mp-btn-compact"
                    onClick={() => activeDay && handleClearDayTasks(activeDay)}
                  >
                    Clear all (this day)
                  </button>
                ) : null}
                <button type="button" className="mp-close-btn" onClick={closeModal}>Close</button>
              </div>
            </div>

            <div className="mp-form">
              <input
                placeholder="Task name"
                value={form.taskName}
                onChange={e => setForm({ ...form, taskName: e.target.value })}
                onKeyDown={e => {
                  if (e.key === 'Enter' && activeDay) {
                    e.preventDefault();
                    handleAdd(activeDay);
                  }
                }}
              />
              <div className="mp-assignee-field">
                <label className="mp-field-label" htmlFor="mp-assignee-select">Assignee</label>
                <select
                  id="mp-assignee-select"
                  className="mp-select"
                  value={assigneeSelectValue}
                  onChange={onAssigneeSelectChange}
                >
                  <option value="">Choose teammate…</option>
                  {teamMembers.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                  <option value="__other__">Other (type name)…</option>
                </select>
                {assigneeSelectValue === '__other__' ? (
                  <input
                    className="mp-assignee-other"
                    placeholder="Type assignee name"
                    value={form.assignee}
                    onChange={e => setForm({ ...form, assignee: e.target.value })}
                  />
                ) : null}
              </div>
              <input placeholder="Effort" value={form.effort} onChange={e => setForm({ ...form, effort: e.target.value })} />
              <input placeholder="Reference ID" value={form.referenceId} onChange={e => setForm({ ...form, referenceId: e.target.value })} />
              <input placeholder="Start date (YYYY-MM-DD)" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
              <input placeholder="End date (YYYY-MM-DD)" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} />
              <input placeholder="Comment" value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })} style={{ gridColumn: '1 / span 2' }} />
              <div className="mp-form-actions">
                <button className="mp-primary-btn" onClick={() => activeDay && handleAdd(activeDay)}>Add Task</button>
              </div>
            </div>

            <div className="mp-bulk-section">
              <h4 className="mp-bulk-title">Bulk Add Tasks (one task per line)</h4>
              <label className="mp-field-label" htmlFor="mp-bulk-assignee-select">Assignee for all bulk tasks</label>
              <select
                id="mp-bulk-assignee-select"
                className="mp-select mp-bulk-assignee-select"
                value={bulkAssigneeSelectValue}
                onChange={onBulkAssigneeSelectChange}
              >
                <option value="">Choose teammate…</option>
                {teamMembers.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
                <option value="__other__">Other (type name)…</option>
              </select>
              {bulkAssigneeSelectValue === '__other__' ? (
                <input
                  className="mp-bulk-assignee-other"
                  placeholder="Type assignee name"
                  value={bulkAssignee}
                  onChange={e => setBulkAssignee(e.target.value)}
                />
              ) : null}
              <textarea
                className="mp-bulk-textarea"
                placeholder={"Enter multiple tasks, one per line:\nDesign login page\nImplement auth API\nWrite unit tests"}
                value={bulkText}
                onChange={e => setBulkText(e.target.value)}
                rows={5}
              />
              <div className="mp-bulk-actions">
                <span className="mp-bulk-hint">{bulkText.split('\n').filter(l => l.trim()).length} task(s) to add</span>
                <button className="mp-primary-btn" onClick={() => activeDay && handleBulkAdd(activeDay)}>Add All Tasks</button>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              {(dayTasks[activeDay] ?? []).length === 0 ? (
                <div style={{ color: '#777' }}>No tasks yet.</div>
              ) : (
                <table className="mp-table">
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Assignee</th>
                      <th>Effort</th>
                      <th>Ref</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Comment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dayTasks[activeDay] ?? []).map(t => (
                      <tr key={t.id}>
                        <td>{t.taskName}</td>
                        <td>{t.assignee}</td>
                        <td>{t.effort}</td>
                        <td>{t.referenceId}</td>
                        <td>{t.startDate}</td>
                        <td>{t.endDate}</td>
                        <td>{t.comment}</td>
                        <td>
                          <button className="mp-danger-btn" onClick={() => handleRemove(activeDay, t.id)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Flat table: assignees (rows) x dates (columns) */}
      {flatTableData.assignees.length > 0 && (
        <div className="mp-flat-table-section">
          <div className="mp-flat-table-header">
            <h3 className="mp-flat-table-title">Monthly Overview &mdash; Assignee &times; Date</h3>
            <div className="mp-flat-table-actions">
              <button type="button" className="mp-primary-btn mp-csv-btn" onClick={exportCSV}>Export CSV</button>
              <button type="button" className="mp-danger-btn mp-btn-compact" onClick={handleClearAllTasks}>
                Clear all tasks
              </button>
            </div>
          </div>
          <div className="mp-flat-table-wrapper">
            <table className="mp-flat-table">
              <thead>
                <tr>
                  <th className="mp-flat-th-assignee">Assignee</th>
                  {columnDateKeys.map(dk => {
                    const d = new Date(dk + 'T12:00:00');
                    const dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <th key={dk} className={`mp-flat-th-date${isWeekend ? ' mp-weekend' : ''}`}>
                        <div className="mp-flat-date-num">{d.getDate()}</div>
                        <div className="mp-flat-date-day">{dayName}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {flatTableData.assignees.map(assignee => (
                  <tr key={assignee}>
                    <td className="mp-flat-td-assignee">{assignee}</td>
                    {columnDateKeys.map(dk => {
                      const tasks = flatTableData.tasksByAssigneeDate[assignee]?.[dk];
                      const d = new Date(dk + 'T12:00:00');
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      return (
                        <td key={dk} className={`mp-flat-td-cell${isWeekend ? ' mp-weekend' : ''}${tasks ? ' mp-has-tasks' : ''}`}>
                          {tasks ? tasks.map((t, i) => (
                            <div key={i} className="mp-flat-task-chip">{t}</div>
                          )) : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bottom aggregated list by date */}
      <div className="mp-task-list">
        {Object.keys(dayTasks).length > 0 ? (
          <>
            <div className="mp-task-list-header-row">
              <h3 className="mp-task-list-title">All Tasks by Date</h3>
              <button type="button" className="mp-danger-btn mp-btn-compact" onClick={handleClearAllTasks}>
                Clear all tasks
              </button>
            </div>
            {Object.entries(dayTasks)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([dateKey, tasks], groupIndex) => (
                <div key={dateKey} className="mp-date-group" style={{ animationDelay: `${groupIndex * 100}ms` }}>
                  <div className="mp-date-header">Date: {dateKey}</div>
                  {tasks.map((t, taskIndex) => (
                    <div key={t.id} className="mp-task-card" style={{ animationDelay: `${taskIndex * 150}ms` }}>
                      <div className="mp-task-header">
                        <div className="mp-task-name">{t.taskName}</div>
                      </div>
                      <div className="mp-task-meta">
                        {t.assignee && (
                          <div className="mp-meta-item assignee">
                            👤 {t.assignee}
                          </div>
                        )}
                        {t.effort && (
                          <div className="mp-meta-item effort">
                            ⏱️ {t.effort}
                          </div>
                        )}
                        {t.referenceId && (
                          <div className="mp-meta-item ref">
                            🔗 {t.referenceId}
                          </div>
                        )}
                        <div className="mp-meta-item dates">
                          📅 {t.startDate} → {t.endDate}
                        </div>
                      </div>
                      {t.comment && (
                        <div className="mp-task-comment">
                          💬 {t.comment}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
          </>
        ) : (
          <div className="mp-empty-state">
            📝 No tasks created yet. Click "Add" on any day to get started!
          </div>
        )}
      </div>
    </div>
  );
}


